
export const currencySymbol = (currency) => (currency === "USD" ? "$" : "₹");

export const DASH = "—";

export const fmtAge = (input) => {
  const variant = input || {};
  const min = Number(variant.minAge) || 0;
  const max = Number(variant.maxAge) || 0;
  if (!min && !max) return DASH;
  return `${min || 18}–${max >= 65 ? "65+" : max}`;
};

export const GEO_BUCKETS = [
  "countries",
  "cities",
  "regions",
  "places",
  "zips",
];

export const EMPTY_GEO = GEO_BUCKETS.reduce(
  (acc, bucket) => ({ ...acc, [bucket]: [] }),
  {}
);

const PLACE_BUCKETS = ["regions", "cities", "places", "zips"];

export const geoLabels = (value) => {
  const geoLocation = value || {};
  const countries = (geoLocation.countries || []).map(String);
  const places = PLACE_BUCKETS.flatMap((bucket) =>
    (geoLocation[bucket] || []).map((item) => {
      const name = item.name || item.key || "";
      if (!item.radius) return name;
      const unit = item.distance_unit === "mile" ? "mi" : "km";
      return `${name} (+${item.radius}${unit})`;
    })
  );
  return [...countries, ...places].filter(Boolean);
};

export const mergeGeoDisplay = (geoLocation, geoLocationDisplay) => {
  const byKey = {};
  (Array.isArray(geoLocationDisplay) ? geoLocationDisplay : []).forEach((d) => {
    if (d && d.key) byKey[d.key] = d;
  });
  const merged = {};
  Object.keys(geoLocation || {}).forEach((bucket) => {
    merged[bucket] = (geoLocation[bucket] || []).map((entry) => {
      if (!entry || typeof entry !== "object") return entry;
      const display = byKey[entry.key] || {};
      return { ...display, ...entry, name: entry.name || display.name || entry.key };
    });
  });
  return merged;
};

// Meta hands advanceTargets back either flat or wrapped in a flexible_spec.
// Everything downstream expects the flat { [type]: [...] } form.
export const unwrapAdvanceTargets = (advanceTargets) =>
  (advanceTargets &&
    advanceTargets.flexible_spec &&
    advanceTargets.flexible_spec[0]) ||
  advanceTargets ||
  {};

// flexible_spec drops Meta's `path`, so the bucket has to be synthesized from
// the type key. Mirrors NewCreateAds.advanceTargetBucket.
export const advanceTargetBucket = (type) => {
  if (type === "interests") return "Interests";
  if (type === "behaviors" || type === "behaviours") return "Behaviors";
  return "Demographics";
};

export const targetLabels = (advanceTargets) => {
  const flat = unwrapAdvanceTargets(advanceTargets);
  return Object.keys(flat).flatMap((bucket) => {
    const entries = flat[bucket];
    if (!Array.isArray(entries)) return [];
    return entries
      .map((entry) => entry && (entry.name || entry.id))
      .filter(Boolean)
      .map((name) => ({ bucket, name: String(name) }));
  });
};

// A variant's geoLocation can be sparse, and mergeGeoDisplay only emits the
// buckets present in its input — so spread over EMPTY_GEO to guarantee all
// five. Callers then never have to guard a missing bucket.
export const normalizeVariantGeo = (variant) => {
  const geoLocation = (variant || {}).geoLocation;
  if (!geoLocation) return { ...EMPTY_GEO };
  return {
    ...EMPTY_GEO,
    ...mergeGeoDisplay(geoLocation, (variant || {}).geoLocationDisplay),
  };
};

export const templateSourceUrl = (template = {}) =>
  (template.generation &&
    template.generation.normalizedInput &&
    template.generation.normalizedInput.url) ||
  template.websiteUrl ||
  "";

export const templatePromptText = (template = {}) =>
  template.prompt ||
  (template.generation &&
    template.generation.normalizedInput &&
    template.generation.normalizedInput.description) ||
  template.description ||
  "";

const nested = (variant, key) => (variant || {})[key] || {};

export const variantCurrency = (variant) =>
  (variant || {}).currency ||
  nested(variant, "analytics").currency ||
  nested(variant, "prediction").currency ||
  "INR";

export const variantDurationDays = (variant) => {
  const sources = [
    (variant || {}).duration,
    nested(variant, "selected").durationDays,
    nested(variant, "prediction").predictedDurationDays,
    nested(variant, "payload").duration,
  ];
  for (const source of sources) {
    if (source === null || source === undefined || source === "") continue;
    const days = Number(source);
    if (Number.isFinite(days)) return days;
  }
  return 0;
};

export const variantGeoLabels = (variant) => {
  const display = (variant || {}).geoLocationDisplay;
  if (Array.isArray(display) && display.length) {
    return geoLabels({ places: display });
  }
  return geoLabels((variant || {}).geoLocation);
};

export const summaryParts = (labels = [], headCount = 2) => ({
  labels,
  head: labels.slice(0, headCount).join(", "),
  all: labels.join(", "),
  hidden: Math.max(0, labels.length - headCount),
});

export const geoSummaryParts = (variant) =>
  summaryParts(variantGeoLabels(variant));

export const targetSummaryParts = (variant) =>
  summaryParts(targetLabels((variant || {}).advanceTargets).map((t) => t.name));
